<script setup lang="ts">
import { computed, ref } from "vue";
import { RouterLink } from "vue-router";
import Button from "primevue/button";
import InputText from "primevue/inputtext";
import FloatLabel from "primevue/floatlabel";
import Checkbox from "primevue/checkbox";
import { useAuthStore, useAuthModal } from "@/features/auth/store";
import { useToast } from "primevue/usetoast";
import Message from "primevue/message";
import Password from "primevue/password";
import Chip from "primevue/chip";
import { useI18n } from "vue-i18n";
import { PRIVACY_POLICY_ROUTE, TERMS_OF_SERVICE_ROUTE } from "@/router/routes";

interface ErrorResponse {
  response?: {
    data?: {
      detail?: string;
    };
  };
}

const isErrorResponse = (error: unknown): error is ErrorResponse => {
  return typeof error === "object" && error !== null && "response" in error;
};

const name = ref("");
const usernameError = ref("");

const email = ref("");
const emailError = ref("");

const password = ref("");
const passwordError = ref("");

const confirmPassword = ref("");
const confirmPasswordError = ref("");

const checked = ref(false);
const loadingState = ref(false);

const toast = useToast();
const { t } = useI18n();

const disableSubmit = computed(() => {
  return (
    email.value === "" ||
    password.value === "" ||
    confirmPassword.value === "" ||
    name.value === "" ||
    !!emailError.value.length ||
    !!passwordError.value.length ||
    !!confirmPasswordError.value.length ||
    !!usernameError.value.length ||
    !checked.value
  );
});

const sixSymbolsValidation = computed(() => password.value.length > 6);
const numberValidation = computed(() => /\d/.test(password.value));
const bigLetterValidation = computed(() => /\p{Lu}/u.test(password.value));
const smallLetterValidation = computed(() => /\p{Ll}/u.test(password.value));
const specialLetterValidation = computed(() =>
  /[!@#$%^&*(),.?":{}|<>]/.test(password.value)
);

const passwordValidations = computed(() => [
  { label: t("password_validation.min_6_chars"), validation: sixSymbolsValidation.value },
  { label: t("password_validation.one_number"), validation: numberValidation.value },
  {
    label: t("password_validation.one_uppercase"),
    validation: bigLetterValidation.value,
  },
  {
    label: t("password_validation.one_lowercase"),
    validation: smallLetterValidation.value,
  },
  {
    label: t("password_validation.one_special_char"),
    validation: specialLetterValidation.value,
  },
]);

function loginHandler() {
  useAuthModal().showAuthModal("signIn");
}

const validateUsername = () => {
  usernameError.value = name.value.length >= 3 ? "" : t("signup.username_error");
};

const validateEmail = () => {
  const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailPattern.test(email.value)) {
    emailError.value = t("signup.email_error");
  } else {
    emailError.value = "";
  }
};

const validatePassword = () => {
  passwordError.value = password.value.length >= 8 ? "" : t("signup.password_error");
};

const validateConfirmPassword = () => {
  if (confirmPassword.value !== password.value) {
    confirmPasswordError.value = t("signup.password_mismatch");
  } else {
    confirmPasswordError.value = "";
  }
};

const handleAccountCreate = async (event?: Event) => {
  if (event) {
    event.preventDefault();
  }

  loadingState.value = true;
  try {
    await useAuthStore().signUp({
      name: name.value,
      email: email.value,
      password: password.value,
    });

    toast.add({
      severity: "success",
      summary: t("signup.account_created"),
      detail: t("signup.email_verification"),
      life: 5000,
    });
  } catch (error: unknown) {
    console.error("Registration failed:", error);
    if (isErrorResponse(error) && error.response?.data?.detail) {
      emailError.value = t("signup.email_admin_confirm");
    } else {
      toast.add({
        severity: "error",
        summary: t("signup.registration_error"),
        detail: t("signup.registration_failed"),
        life: 5000,
      });
    }
  } finally {
    loadingState.value = false;
  }
};
</script>

<template>
  <div class="sign-up__content">
    <form class="sign-up__content__wrapper" @submit.prevent="handleAccountCreate">
      <div class="sign-up__inputs">
        <div>
          <FloatLabel>
            <InputText
              id="name"
              v-model="name"
              @input="validateUsername"
              name="name"
              autocomplete="name"
              class="name"
              :pt="{
                root: {
                  style: {
                    width: '100%',
                    borderRadius: '8px',
                  },
                },
              }"
            />
            <label for="name">{{ t("signup.username_label") }}</label>
          </FloatLabel>
          <Message
            v-if="!!usernameError"
            severity="error"
            size="small"
            :pt="{
              root: {
                style: {
                  outline: 'none',
                },
              },
              content: {
                style: {
                  padding: '0',
                  background: 'transparent',
                },
              },
              text: {
                style: {
                  paddingLeft: '16px',
                  fontSize: '12px',
                },
              },
            }"
            >{{ usernameError }}
          </Message>
        </div>
        <div>
          <FloatLabel>
            <InputText
              v-model="email"
              id="email"
              @input="validateEmail"
              name="email"
              autocomplete="email"
              class="email"
              :pt="{
                root: {
                  style: {
                    width: '100%',
                    borderRadius: '8px',
                  },
                },
                input: {
                  autocomplete: 'email',
                  'data-form-type': 'email',
                },
              }"
            />
            <label for="email">{{ t("signup.email_label") }}</label>
          </FloatLabel>
          <Message
            v-if="!!emailError"
            severity="error"
            size="small"
            :pt="{
              root: {
                style: {
                  outline: 'none',
                },
              },
              content: {
                style: {
                  padding: '0',
                  background: 'transparent',
                },
              },
              text: {
                style: {
                  paddingLeft: '16px',
                  fontSize: '12px',
                },
              },
            }"
            >{{ emailError }}
          </Message>
        </div>
        <div>
          <FloatLabel>
            <Password
              v-model="password"
              @input="validatePassword"
              toggle-mask
              :inputProps="{
                id: 'password',
                name: 'password',
                autocomplete: 'new-password',
              }"
              :feedback="password.length >= 1"
              class="password"
              :pt="{
                root: {
                  style: {
                    width: '100%',
                    borderRadius: '8px',
                  },
                },
                pcInput: {
                  root: {
                    style: {
                      width: '100%',
                      borderRadius: '8px',
                    },
                  },
                  input: {
                    id: 'password',
                    name: 'password',
                    autocomplete: 'new-password',
                    'data-form-type': 'password',
                    required: true,
                  },
                },
              }"
            >
              <template #footer>
                <div class="password__validation-chips-wrapper">
                  <Chip
                    v-for="(item, index) in passwordValidations"
                    :key="index"
                    :label="item.label"
                    :icon="`pi ${
                      item.validation ? 'pi-chevron-circle-down' : 'pi-circle'
                    }`"
                    :pt="{
                      root: {
                        style: {
                          backgroundColor: item.validation
                            ? 'rgba(14, 105, 17, 0.05)'
                            : 'rgba(26, 33, 51, 0.05)',
                          color: item.validation
                            ? 'var(--green-100)'
                            : 'var(--primary-blue-80)',
                        },
                      },
                      icon: {
                        style: {
                          color: item.validation
                            ? 'var(--green-100)'
                            : 'var(--primary-blue-80)',
                        },
                      },
                    }"
                  />
                </div>
              </template>
            </Password>
            <label for="password">{{ t("signup.password_label") }}</label>
          </FloatLabel>
          <Message
            v-if="!!passwordError"
            severity="error"
            size="small"
            :pt="{
              root: {
                style: {
                  outline: 'none',
                },
              },
              content: {
                style: {
                  padding: '0',
                  background: 'transparent',
                },
              },
              text: {
                style: {
                  paddingLeft: '16px',
                  fontSize: '12px',
                },
              },
            }"
            >{{ passwordError }}
          </Message>
        </div>
        <div>
          <FloatLabel>
            <Password
              v-model="confirmPassword"
              @input="validateConfirmPassword"
              :inputProps="{
                id: 'confirmPassword',
                name: 'confirmPassword',
                autocomplete: 'new-password',
              }"
              class="password"
              :feedback="false"
              toggle-mask
              :pt="{
                root: {
                  style: {
                    width: '100%',
                    borderRadius: '8px',
                  },
                },
                pcInput: {
                  root: {
                    style: {
                      width: '100%',
                      borderRadius: '8px',
                    },
                  },
                  input: {
                    id: 'password',
                    name: 'password',
                    autocomplete: 'new-password',
                    'data-form-type': 'password',
                    required: true,
                  },
                },
              }"
            >
            </Password>
            <label for="confirmPassword">{{ t("signup.confirm_password_label") }}</label>
          </FloatLabel>
          <Message
            v-if="!!confirmPasswordError"
            severity="error"
            size="small"
            :pt="{
              root: {
                style: {
                  outline: 'none',
                },
              },
              content: {
                style: {
                  padding: '0',
                  background: 'transparent',
                },
              },
              text: {
                style: {
                  paddingLeft: '16px',
                  fontSize: '12px',
                },
              },
            }"
            >{{ confirmPasswordError }}
          </Message>
        </div>
      </div>
      <div class="policy">
        <Checkbox
          v-model="checked"
          input-id="signup-policy"
          :pt="{
            root: {
              style: {
                width: '24px',
                height: '24px',
              },
            },
          }"
          :binary="true"
        />
        <label
          class="policy__label"
          for="signup-policy"
        >
          {{ t("signup.privacy_policy_prefix") }}
          <RouterLink
            class="policy__link"
            :to="PRIVACY_POLICY_ROUTE"
            target="_blank"
            rel="noopener noreferrer"
            @click.stop
          >
            {{ t("signup.privacy_policy_link") }}
          </RouterLink>
          {{ t("signup.privacy_policy_and") }}
          <RouterLink
            class="policy__link"
            :to="TERMS_OF_SERVICE_ROUTE"
            target="_blank"
            rel="noopener noreferrer"
            @click.stop
          >
            {{ t("signup.terms_of_service_link") }}
          </RouterLink>
        </label>
      </div>
      <div class="sign-up__submit_btn__wrapper">
        <Button
          class="sign-up-submit"
          :label="t('signup.create_account_button')"
          type="submit"
          :disabled="disableSubmit"
          :loading="loadingState"
        />
      </div>
    </form>
  </div>
</template>
<style scoped>
.password__validation-chips-wrapper {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  max-width: 280px;
}

.sign-up__content {
  display: flex;
  border-radius: 40px;
  padding-top: 12px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;
}

.sign-up__content__wrapper {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.sign-up__inputs {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.name,
.email,
.password {
  width: 100%;
}

.policy {
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 8px;
  padding: 12px 0 40px 0;
}

.policy__label {
  flex: 1 1 auto;
  margin: 0;
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 1.5;
  color: rgba(35, 47, 69, 0.9);
  cursor: pointer;
}

/* Figma Create account 1:11428 — active blue #6e85cb, Bold */
.policy__link {
  color: var(--active-100, #6e85cb);
  font-weight: 700;
  text-decoration: none;
}

.policy__link:hover {
  text-decoration: underline;
}

.sign-up__submit_btn__wrapper {
  display: flex;
  width: 100%;
}

.sign-up-submit {
  width: 100%;
  border-radius: 100px;
}

.old_user {
  display: flex;
  align-items: center;
  gap: 8px;
}
</style>
